import './BulkTagConfigStep.scss';
import * as Dfns from 'date-fns/fp';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import { DATE_FORMAT, TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Dropdown } from '@Components/Dropdown';
import { Placement, Size } from '@Components/designConstants';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { DeviceNames, hasDeviceTags } from '../utils';
import { DimensionsPicker } from './DimensionsPicker';
import { AdServer, TagData } from './utils';
import { FLASHTALKING_DSP_TO_VENDOR_MAP } from './TagCreationForm';
import { DIMENSION_OPTIONS } from './generate-view-constants';

interface PerTagFields {
  vendor: string;
  placementType: '' | 'Audio' | 'Pixel' | 'VAST' | 'Display';
  transcodeVendor: string;
  dimensionsMap: Record<string, boolean>;
}

interface BulkTagConfigStepProps {
  allTags: TagData[];
  onSubmit: (updatedTags: TagData[]) => void;
  flashtalkingCampaigns: { campaign_id: number; campaign: string; is_managed_service: boolean }[];
}

const getVendorOptions = (tag: TagData, adServer: AdServer): string[] => {
  const ftVendors = tag.flashtalking_vendors ?? [];
  const tagFlashtalkingVendors = [...new Set(ftVendors.map(v => v.name))];
  if (tag.dsp && adServer === 'Flashtalking' && FLASHTALKING_DSP_TO_VENDOR_MAP[tag.dsp] == null) {
    return [];
  }
  if (tag.dsp && tag.network !== 'SPOTIFY' && adServer === 'Flashtalking') {
    return [...new Set([FLASHTALKING_DSP_TO_VENDOR_MAP[tag.dsp], ...tagFlashtalkingVendors])];
  }
  if (adServer === 'Flashtalking') {
    return tagFlashtalkingVendors;
  }
  return [...new Set(tag.vendorOptions ?? [])];
};

const getPlacementTypeOptions = (
  tag: TagData,
  vendor: string
): { label: string; value: string }[] => {
  const defaultOptions: Record<string, { label: string; value: string }> = {
    audio_vast: { label: 'Audio VAST', value: 'Audio' },
    pixel: { label: 'Pixel', value: 'Pixel' },
    video_vast: { label: 'Video VAST', value: 'VAST' },
    display: { label: 'Display', value: 'Display' }
  };
  if (vendor) {
    for (const vendorOption of tag.flashtalking_vendors ?? []) {
      if (vendor === vendorOption.name) {
        const options: { label: string; value: string }[] = [];
        for (const type of vendorOption.placement_types) {
          if (defaultOptions[type]) {
            options.push(defaultOptions[type]);
          }
        }
        return options;
      }
    }
  }
  return Object.values(defaultOptions);
};

const emptyDimensionsMap = DIMENSION_OPTIONS.reduce(
  (acc, opt) => ({ ...acc, [opt.value]: false }),
  {} as Record<string, boolean>
);

export const BulkTagConfigStep = ({
  allTags,
  onSubmit,
  flashtalkingCampaigns
}: BulkTagConfigStepProps) => {
  const adServer: AdServer = 'Flashtalking';

  const [campaignID, setCampaignID] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [deviceMap, setDeviceMap] = useState<Record<string, boolean>>({
    [DeviceNames.MOBILE]: true,
    [DeviceNames.CTV]: true,
    [DeviceNames.DESKTOP]: true
  });
  const [lengthMap, setLengthMap] = useState<Record<string, boolean>>({
    '15': false,
    '30': false,
    '60': false,
    '120': false
  });
  const [formFail, setFormFail] = useState(false);

  const showDevicePicker = useMemo(() => allTags.some(t => hasDeviceTags(t)), [allTags]);
  const showLengthPicker = useMemo(() => allTags.some(t => t.splitLength), [allTags]);

  const [perTagState, setPerTagState] = useState<Record<string, PerTagFields>>(() =>
    Object.fromEntries(
      allTags.map(tag => {
        const opts = getVendorOptions(tag, 'Flashtalking');
        let autoVendor = '';
        if (tag.dsp && FLASHTALKING_DSP_TO_VENDOR_MAP[tag.dsp]) {
          autoVendor = FLASHTALKING_DSP_TO_VENDOR_MAP[tag.dsp];
        } else if (opts.length === 1) {
          [autoVendor] = opts;
        }
        return [
          tag.derivedID,
          {
            vendor: autoVendor,
            placementType: '' as const,
            transcodeVendor: '',
            dimensionsMap: { ...emptyDimensionsMap }
          }
        ];
      })
    )
  );

  const campaignOptions = useMemo(
    () =>
      flashtalkingCampaigns
        .filter(c => !c.is_managed_service)
        .map(campaign => ({
          label: `${campaign.campaign} (${campaign.campaign_id})`,
          value: `${campaign.campaign_id}`
        })),
    [flashtalkingCampaigns]
  );

  useEffect(() => {
    if (flashtalkingCampaigns.length === 1) {
      setCampaignID(flashtalkingCampaigns[0].campaign_id);
    }
  }, [flashtalkingCampaigns]);

  const updatePerTagField = useCallback(
    <K extends keyof PerTagFields>(derivedID: string, field: K, value: PerTagFields[K]) => {
      setPerTagState(prev => ({
        ...prev,
        [derivedID]: { ...prev[derivedID], [field]: value }
      }));
    },
    []
  );

  const formFailMessage = useMemo(() => {
    if (!formFail) return '';
    if (!campaignID) return 'Please select a campaign.';
    if (!startDate || !endDate) return 'Please select a date range.';
    if (showDevicePicker && !Object.values(deviceMap).includes(true)) {
      return 'Please select at least one device.';
    }
    if (showLengthPicker && !Object.values(lengthMap).includes(true)) {
      return 'Please select at least one length.';
    }
    if (allTags.some(t => !perTagState[t.derivedID]?.vendor)) {
      return 'Please select a vendor for all tags.';
    }
    if (allTags.some(t => !perTagState[t.derivedID]?.placementType)) {
      return 'Please select a placement type for all tags.';
    }
    if (
      allTags.some(
        t =>
          perTagState[t.derivedID]?.placementType === 'Display' &&
          !Object.values(perTagState[t.derivedID]?.dimensionsMap ?? {}).includes(true)
      )
    ) {
      return 'Please select dimensions for all Display placement tags.';
    }
    return 'Please fill in all required fields.';
  }, [
    formFail,
    campaignID,
    startDate,
    endDate,
    showDevicePicker,
    deviceMap,
    showLengthPicker,
    lengthMap,
    allTags,
    perTagState
  ]);

  const attemptSubmit = useCallback(() => {
    const hasValidDevices = !showDevicePicker || Object.values(deviceMap).includes(true);
    const hasValidLengths = !showLengthPicker || Object.values(lengthMap).includes(true);
    const allTagsValid = allTags.every(tag => {
      const pts = perTagState[tag.derivedID];
      if (!pts?.vendor || !pts?.placementType) return false;
      if (pts.placementType === 'Display') {
        return Object.values(pts.dimensionsMap).includes(true);
      }
      return true;
    });
    const isValid =
      Boolean(campaignID) &&
      Boolean(startDate) &&
      Boolean(endDate) &&
      hasValidDevices &&
      hasValidLengths &&
      allTagsValid;

    if (!isValid) {
      setFormFail(true);
      return;
    }

    const updatedTags = allTags.map(tag => ({
      ...tag,
      adServer,
      campaignID,
      startDate,
      endDate,
      ...(showDevicePicker && hasDeviceTags(tag) ? { deviceMap } : {}),
      ...(showLengthPicker && tag.splitLength ? { lengthMap } : {}),
      vendor: perTagState[tag.derivedID].vendor,
      placementType: perTagState[tag.derivedID].placementType,
      transcodeVendor: perTagState[tag.derivedID].transcodeVendor,
      ...(perTagState[tag.derivedID].placementType === 'Display'
        ? { dimensionsMap: perTagState[tag.derivedID].dimensionsMap }
        : {})
    }));

    onSubmit(updatedTags);
  }, [
    adServer,
    allTags,
    campaignID,
    deviceMap,
    endDate,
    lengthMap,
    onSubmit,
    perTagState,
    showDevicePicker,
    showLengthPicker,
    startDate
  ]);

  return (
    <div className='bulk-tag-config-step'>
      <Modal.Header closeButton>
        <Modal.Title>{`Configure Tags for ${allTags.length} Derived IDs`}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='bulk-tag-config-body'>
        <div className='shared-section'>
          <div className='section-label'>Shared Settings</div>
          <div className='selection-div'>
            <div className='selection-title'>Ad Server:</div>
            <div>Flashtalking</div>
          </div>
          <div className='selection-div'>
            <div className='selection-title'>Campaign:</div>
            {campaignOptions.length > 0 ? (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                onChange={e => setCampaignID(parseInt(e, 10))}
                options={campaignOptions}
                size={Size.SM}
                value={`${campaignID ?? ''}`}
              />
            ) : (
              <span className={formFail && !campaignID ? 'invalid' : ''}>
                No campaigns available
              </span>
            )}
          </div>
          <div className='selection-div'>
            <div className='selection-title'>Start &amp; End Date:</div>
            <DatePicker
              range={{
                start: startDate ?? TODAY,
                end:
                  endDate ??
                  Dfns.format(
                    DATE_FORMAT,
                    new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                  )
              }}
              onChange={newDates => {
                if (newDates.start && newDates.end) {
                  setStartDate(newDates.start);
                  setEndDate(newDates.end);
                }
              }}
              isOutsideRange={() => false}
            />
          </div>
          {showDevicePicker && (
            <div className='selection-div'>
              <div className='selection-title'>Devices:</div>
              <SegmentedControlButton
                options={[DeviceNames.MOBILE, DeviceNames.CTV, DeviceNames.DESKTOP]}
                selectedOptions={Object.keys(deviceMap).filter(d => deviceMap[d])}
                isExclusiveSelect={false}
                onChange={device => setDeviceMap(prev => ({ ...prev, [device]: !prev[device] }))}
                size={Size.SM}
              />
            </div>
          )}
          {showLengthPicker && (
            <div className='selection-div'>
              <div className='selection-title'>Lengths:</div>
              <SegmentedControlButton
                options={['6', '15', '30', '60', '120']}
                selectedOptions={Object.keys(lengthMap).filter(l => lengthMap[l])}
                isExclusiveSelect={false}
                onChange={length => setLengthMap(prev => ({ ...prev, [length]: !prev[length] }))}
                size={Size.SM}
              />
            </div>
          )}
        </div>
        <div className='per-tag-list'>
          <div className='section-label'>Per-Tag Settings</div>
          {allTags.map(tag => {
            const pts = perTagState[tag.derivedID];
            const vendorOptions = getVendorOptions(tag, adServer);
            const placementTypeOptions = getPlacementTypeOptions(tag, pts.vendor);
            const showTranscodeVendor =
              pts.placementType !== 'Pixel' &&
              Object.values(FLASHTALKING_DSP_TO_VENDOR_MAP).includes(pts.vendor) &&
              adServer === 'Flashtalking';
            const showDimensions = pts.placementType === 'Display';
            const tagIsValid = Boolean(pts?.vendor && pts?.placementType);

            return (
              <div
                key={tag.derivedID}
                className={cn('per-tag-row', { 'per-tag-row-invalid': formFail && !tagIsValid })}
              >
                <div className='tag-identity'>
                  <span className='tag-derived-id'>{tag.derivedID}</span>
                  <span className='tag-meta'>
                    {tag.network}
                    {tag.dsp ? ` — DSP: ${tag.dsp}` : ''}
                  </span>
                </div>
                <div className='selection-div'>
                  <div className='selection-title'>Vendor:</div>
                  {vendorOptions.length > 0 ? (
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      onChange={e => updatePerTagField(tag.derivedID, 'vendor', e)}
                      options={vendorOptions.map(v => ({ label: v, value: v }))}
                      size={Size.SM}
                      value={pts.vendor}
                    />
                  ) : (
                    <span className='no-vendors'>No vendors available for {tag.network}</span>
                  )}
                </div>
                <div className='selection-div'>
                  <div className='selection-title'>Placement Type:</div>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    onChange={e =>
                      updatePerTagField(
                        tag.derivedID,
                        'placementType',
                        e as PerTagFields['placementType']
                      )
                    }
                    options={placementTypeOptions}
                    size={Size.SM}
                    value={pts.placementType}
                  />
                </div>
                {showTranscodeVendor && (
                  <div className='selection-div'>
                    <div className='selection-title'>Transcode Vendor:</div>
                    {tag.flashtalking_vendors ? (
                      <Dropdown
                        overlayPlacement={Placement.RIGHT}
                        onChange={e => updatePerTagField(tag.derivedID, 'transcodeVendor', e)}
                        options={tag.flashtalking_vendors.map(v => ({
                          label: v.pretty_name,
                          value: v.name
                        }))}
                        size={Size.SM}
                        value={pts.transcodeVendor}
                      />
                    ) : (
                      <span className='no-vendors'>No vendors available for {tag.network}</span>
                    )}
                  </div>
                )}
                {showDimensions && (
                  <div className='selection-div'>
                    <div className='selection-title'>Dimensions:</div>
                    <DimensionsPicker
                      dimensionsMap={pts.dimensionsMap}
                      onChange={dimensionsMap =>
                        updatePerTagField(tag.derivedID, 'dimensionsMap', dimensionsMap)
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {formFailMessage && (
          <Alert variant='danger' className='alert'>
            {formFailMessage}
          </Alert>
        )}
        <Button onClick={attemptSubmit}>Next</Button>
      </Modal.Footer>
    </div>
  );
};
